import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Breadcrumbs,
  Link,
  Skeleton,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  History as HistoryIcon,
  Comment as CommentIcon,
  AttachFile as AttachFileIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  LockOutlined as LockIcon,
  Public as PublicIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  FileCopy as FileCopyIcon,
  PriorityHigh as PriorityIcon,
  Category as CategoryIcon,
  Source as SourceIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import useFetchObject from '../../api/useFetchObject';
import useDelete from '../../api/useDelete';
import { useAuth } from '../../context/AuthContext';
import { styled, useTheme, alpha } from '@mui/material/styles';
import LocalizedDate from '../../components/common/LocalizedDate';

// RTL-aware styled components
const RTLButton = styled(Button)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
  borderRadius: 8,
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 500,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: theme.shadows[2]
  }
}));

const RTLIconButton = styled(IconButton)(({ theme, isRtl }) => ({
  ...(isRtl && {
    transform: 'scaleX(-1)',
  }),
}));

// Enhanced Card component
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 4px 20px 0 ' + alpha(theme.palette.grey[500], 0.2),
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    boxShadow: '0 8px 25px 0 ' + alpha(theme.palette.grey[500], 0.3),
  }
}));

// Label-value pair component
const LabelValue = ({ label, value, icon, isRtl, valueColor }) => (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'flex-start', 
    mb: 2.5,
    flexDirection: isRtl ? 'row' : 'row-reverse',
    textAlign: isRtl ? 'right' : 'left'
  }}>
    {icon && (
      <Box sx={{ 
        mr: isRtl ? 2 : 0, 
        ml: isRtl ? 0 : 2, 
        color: 'primary.main',
        mt: 0.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: '50%',
        bgcolor: alpha('#1976d2', 0.08)
      }}>
        {icon}
      </Box>
    )}
    <Box sx={{ flexGrow: 1, textAlign: isRtl ? 'right' : 'left' }}>
      <Typography 
        variant="body2" 
        color="text.secondary" 
        gutterBottom
        sx={{ 
          fontSize: '0.8rem',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 0.5
        }}
      >
        {label}
      </Typography>
      <Typography 
        variant="body1" 
        sx={{ 
          fontWeight: 500,
          color: valueColor || 'text.primary',
          wordBreak: 'break-word',
          lineHeight: 1.5
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

// Common section title style
const getSectionTitleStyle = (isRtl) => ({
  mb: 3, 
  fontWeight: 600,
  fontSize: '1.1rem',
  color: 'primary.main',
  position: 'relative',
  pb: 1,
  textAlign: isRtl ? 'right' : 'left',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: isRtl ? 'auto' : 0,
    right: isRtl ? 0 : 'auto',
    width: 40,
    height: 3,
    bgcolor: 'primary.main',
    borderRadius: 1.5
  }
});

const DocumentView = () => {
  const { t, i18n } = useTranslation(['documents', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token;
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [tabValue, setTabValue] = useState(0);

  // Fetch document data
  const {
    data: document,
    isLoading,
    isError,
    error,
    refetch
  } = useFetchObject('documents', 'documents', id, token);

  // Delete handler
  const { handleDelete, ConfirmDialog } = useDelete('documents', token, () => {
    navigate('/documents');
  });

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Document types and statuses dictionaries
  const typesDictionary = {
    'مکتوب': t('documents:مکتوب', 'مکتوب'),
    'استعلام': t('documents:استعلام', 'استعلام'),
    'پیشنهاد': t('documents:پیشنهاد', 'پیشنهاد'),
    'گزارش': t('documents:گزارش', 'گزارش'),
    'LETTER': t('documents:type_letter', 'Letter'),
    'MEMO': t('documents:type_memo', 'Memo'),
    'REPORT': t('documents:type_report', 'Report'),
    'PROPOSAL': t('documents:type_proposal', 'Proposal'),
    'REQUEST': t('documents:type_request', 'Request'),
    'APPROVAL': t('documents:type_approval', 'Approval'),
    'CONTRACT': t('documents:type_contract', 'Contract'),
    'OTHER': t('documents:type_other', 'Other')
  };

  const prioritiesDictionary = {
    'عاجل': t('documents:عاجل', 'عاجل'),
    'اطمینانیه': t('documents:اطمینانیه', 'اطمینانیه'),
    'ابلاغیه': t('documents:ابلاغیه', 'ابلاغیه'),
    'محرم': t('documents:محرم', 'محرم'),
    'عادي': t('documents:عادي', 'عادي'),
    'LOW': t('documents:priority_low', 'Low'),
    'MEDIUM': t('documents:priority_medium', 'Medium'),
    'HIGH': t('documents:priority_high', 'High'),
    'URGENT': t('documents:priority_urgent', 'Urgent')
  };

  const sourceTypesDictionary = {
    'INTERNAL': t('documents:source_internal', 'Internal'),
    'EXTERNAL': t('documents:source_external', 'External'),
    'PROVINCIAL': t('documents:source_provincial', 'Provincial')
  };

  const statusDictionary = {
    'DRAFT': t('documents:status_draft', 'Draft'),
    'IN_PROGRESS': t('documents:status_in_progress', 'In Progress'),
    'COMPLETED': t('documents:status_completed', 'Completed'),
    'REJECTED': t('documents:status_rejected', 'Rejected'),
    'APPROVED': t('documents:status_approved', 'Approved'),
    'CANCELLED': t('documents:status_cancelled', 'Cancelled')
  };

  // Helper functions for colors
  const getStatusColor = (status) => ({
    'DRAFT': 'default',
    'IN_PROGRESS': 'primary',
    'COMPLETED': 'success',
    'REJECTED': 'error',
    'APPROVED': 'success',
    'CANCELLED': 'secondary'
  }[status] || 'default');

  const getPriorityColor = (priority) => ({
    'عادي': 'default',
    'اطمینانیه': 'info',
    'ابلاغیه': 'warning',
    'محرم': 'secondary',
    'عاجل': 'error',
    'LOW': 'default',
    'MEDIUM': 'info',
    'HIGH': 'warning',
    'URGENT': 'error'
  }[priority] || 'default');

  return (
    <Box dir={isRtl ? 'rtl' : 'ltr'} sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        
        
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          <RTLButton
            variant="outlined"
            startIcon={!isRtl && <ArrowBack />}
            endIcon={isRtl && <ArrowBack />}
            component={RouterLink}
            to="/documents"
            isRtl={isRtl}
            sx={{ 
              borderRadius: 8, 
              boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
              borderColor: 'divider',
              '&:hover': {
                boxShadow: '0 4px 12px 0 rgba(0,0,0,0.15)',
                bgcolor: 'background.paper',
                borderColor: 'primary.main'
              },
              '& .MuiButton-startIcon': {
                marginRight: isRtl ? 0 : '12px',
                marginLeft: isRtl ? '12px' : 0
              },
              '& .MuiButton-endIcon': {
                marginLeft: isRtl ? 0 : '12px',
                marginRight: isRtl ? '12px' : 0
              }
            }}
          >
            {t('common:back')}
          </RTLButton>
          
          {document && (
            <>
              <RTLButton
                variant="contained"
                startIcon={!isRtl && <EditIcon />}
                endIcon={isRtl && <EditIcon />}
                component={RouterLink}
                to={`/documents/${id}/edit`}
                isRtl={isRtl}
                sx={{ 
                  borderRadius: 8, 
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 12px 0 rgba(0,0,0,0.15)'
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: isRtl ? 0 : '12px',
                    marginLeft: isRtl ? '12px' : 0
                  },
                  '& .MuiButton-endIcon': {
                    marginLeft: isRtl ? 0 : '12px',
                    marginRight: isRtl ? '12px' : 0
                  }
                }}
              >
                {t('common:edit')}
              </RTLButton>
              
              <RTLButton
                variant="outlined"
                startIcon={!isRtl && <DeleteIcon />}
                endIcon={isRtl && <DeleteIcon />}
                onClick={() => handleDelete(id)}
                isRtl={isRtl}
                color="error"
                sx={{ 
                  borderRadius: 8, 
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 12px 0 rgba(0,0,0,0.15)'
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: isRtl ? 0 : '12px',
                    marginLeft: isRtl ? '12px' : 0
                  },
                  '& .MuiButton-endIcon': {
                    marginLeft: isRtl ? 0 : '12px',
                    marginRight: isRtl ? '12px' : 0
                  }
                }}
              >
                {t('common:delete')}
              </RTLButton>
            </>
          )}
        </Box>



        <Box>
          <Breadcrumbs 
            separator="›" 
            aria-label="breadcrumb" 
            sx={{ 
              mb: 1,
              '& .MuiBreadcrumbs-separator': {
                mx: 1,
                color: 'text.secondary'
              },
              '& .MuiBreadcrumbs-li': {
                display: 'flex',
                alignItems: 'center'
              }
            }}
          >
            <Link 
              component={RouterLink} 
              to="/documents" 
              underline="hover" 
              color="inherit"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.875rem',
                '&:hover': {
                  color: 'primary.main'
                },
                flexDirection: isRtl ? 'row-reverse' : 'row'
              }}
            >
              <FileCopyIcon sx={{ 
                mx: 0.5, 
                fontSize: '0.9rem',
                marginRight: isRtl ? 0 : '8px',
                marginLeft: isRtl ? '8px' : 0
              }} />
              {t('documents:all_documents', 'All Documents')}
            </Link>
            <Typography 
              color="text.primary"
              sx={{ 
                fontSize: '0.875rem',
                fontWeight: 500 
              }}
            >
              {isLoading ? <Skeleton width={100} /> : document?.title || t('documents:document_details', 'Document Details')}
            </Typography>
          </Breadcrumbs>
          
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              textAlign: isRtl ? 'right' : 'left',
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2rem' },
              color: 'primary.main',
              lineHeight: 1.2,
              mt: 1
            }}
          >
            {isLoading ? <Skeleton width={300} /> : document?.title || t('documents:document_details', 'Document Details')}
          </Typography>
        </Box>
      </Box>

      {isError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)'
          }}
        >
          {error?.message || t('common:error_loading')}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ width: '100%' }}>
          <StyledCard sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1 }} />
            </CardContent>
          </StyledCard>
        </Box>
      ) : document ? (
        <>
          {/* Document status and metadata */}
          <StyledCard sx={{ mb: 3, position: 'relative', overflow: 'visible' }}>
            <Box 
              sx={{ 
                position: 'absolute', 
                top: -20, 
                right: isRtl ? 'auto' : 20, 
                left: isRtl ? 20 : 'auto',
                zIndex: 1, 
                display: 'flex',
                gap: 1
              }}
            >
              <Chip 
                icon={<VisibilityIcon />}
                label={statusDictionary[document.status] || document.status}
                color={getStatusColor(document.status)}
                sx={{ 
                  fontWeight: 'bold', 
                  borderRadius: 4,
                  height: 36,
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.15)',
                  '& .MuiChip-icon': {
                    fontSize: '1rem',
                    marginRight: isRtl ? 0 : '8px',
                    marginLeft: isRtl ? '8px' : 0
                  }
                }}
              />
              
              <Chip 
                icon={<PriorityIcon />}
                label={document.priority ? prioritiesDictionary[document.priority] || document.priority : t('documents:priority_normal', 'Normal')}
                color={getPriorityColor(document.priority)}
                sx={{ 
                  fontWeight: 'bold', 
                  borderRadius: 4,
                  height: 36,
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.15)',
                  '& .MuiChip-icon': {
                    fontSize: '1rem',
                    marginRight: isRtl ? 0 : '8px',
                    marginLeft: isRtl ? '8px' : 0
                  }
                }}
              />
              
              {document.is_confidential && (
                <Chip 
                  icon={<LockIcon />} 
                  label={t('documents:confidential', 'Confidential')}
                  color="error"
                  sx={{ 
                    fontWeight: 'bold',
                    borderRadius: 4,
                    height: 36,
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.15)',
                    '& .MuiChip-icon': {
                      fontSize: '1rem',
                      marginRight: isRtl ? 0 : '8px',
                      marginLeft: isRtl ? '8px' : 0
                    }
                  }}
                />
              )}
            </Box>
            
            <CardContent sx={{ pt: 5, px: { xs: 2, sm: 3, md: 4 }, pb: { xs: 2, sm: 3, md: 4 } }}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={8}>
                  <Box sx={{ mb: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={getSectionTitleStyle(isRtl)}
                    >
                      {t('documents:basic_information', 'Basic Information')}
                    </Typography>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <LabelValue 
                          label={t('documents:title')} 
                          value={document.title}
                          icon={<DescriptionIcon fontSize="small" />}
                          isRtl={isRtl}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <LabelValue 
                          label={t('documents:reference_number')} 
                          value={document.reference_number}
                          icon={<FileCopyIcon fontSize="small" />}
                          isRtl={isRtl}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <LabelValue 
                          label={t('documents:type')} 
                          value={typesDictionary[document.type] || document.type}
                          icon={<CategoryIcon fontSize="small" />}
                          isRtl={isRtl}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <LabelValue 
                          label={t('documents:priority')} 
                          value={prioritiesDictionary[document.priority] || document.priority}
                          icon={<PriorityIcon fontSize="small" />}
                          isRtl={isRtl}
                          valueColor={getPriorityColor(document.priority) + '.main'}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <LabelValue 
                          label={t('documents:document_date')} 
                          value={document.document_date ? 
                            <LocalizedDate date={document.document_date} /> : 
                            '—'
                          }
                          icon={<CalendarIcon fontSize="small" />}
                          isRtl={isRtl}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <LabelValue 
                          label={t('documents:number_of_pages')} 
                          value={document.number_of_pages}
                          icon={<FileCopyIcon fontSize="small" />}
                          isRtl={isRtl}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <LabelValue 
                          label={t('documents:doc_source_type')} 
                          value={sourceTypesDictionary[document.source_type] || document.source_type}
                          icon={<SourceIcon fontSize="small" />}
                          isRtl={isRtl}
                        />
                      </Grid>
                      
                      {document.source_type === 'EXTERNAL' && document.external_source_name && (
                        <Grid item xs={12} sm={6}>
                          <LabelValue 
                            label={t('documents:doc_external_source')} 
                            value={document.external_source_name}
                            icon={<PublicIcon fontSize="small" />}
                            isRtl={isRtl}
                          />
                        </Grid>
                      )}
                      
                      {document.due_date && (
                        <Grid item xs={12} sm={6}>
                          <LabelValue 
                            label={t('documents:due_date')} 
                            value={<LocalizedDate date={document.due_date} />}
                            icon={<ScheduleIcon fontSize="small" />}
                            isRtl={isRtl}
                          />
                        </Grid>
                      )}
                      
                      {document.department && (
                        <Grid item xs={12} sm={6}>
                          <LabelValue 
                            label={t('documents:department')} 
                            value={document.department.name}
                            icon={<PersonIcon fontSize="small" />}
                            isRtl={isRtl}
                          />
                        </Grid>
                      )}
                      
                      {document.directorate && (
                        <Grid item xs={12} sm={6}>
                          <LabelValue 
                            label={t('documents:directorate')} 
                            value={document.directorate.name}
                            icon={<PersonIcon fontSize="small" />}
                            isRtl={isRtl}
                          />
                        </Grid>
                      )}
                      
                      {document.deputy_ministry && (
                        <Grid item xs={12} sm={6}>
                          <LabelValue 
                            label={t('documents:deputy_ministry')} 
                            value={document.deputy_ministry.name}
                            icon={<PersonIcon fontSize="small" />}
                            isRtl={isRtl}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={getSectionTitleStyle(isRtl)}
                    >
                      {t('documents:document_description', 'Document Description')}
                    </Typography>
                    
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 1, 
                        bgcolor: alpha(theme.palette.background.default, 0.7),
                        borderRadius: 3,
                        minHeight: 100,
                        whiteSpace: 'pre-wrap',
                        border: '1px solid',
                        borderColor: alpha(theme.palette.divider, 0.5),
                        fontFamily: theme.typography.fontFamily,
                        fontSize: '0.95rem',
                        lineHeight: 1.6
                      }}
                    >
                      {document.description || t('documents:no_description_available', 'No description available')}
                    </Paper>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    bgcolor: alpha(theme.palette.background.default, 0.7),
                    p: 3, 
                    borderRadius: 3,
                    height: '100%',
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.5)
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={getSectionTitleStyle(isRtl)}
                    >
                      {t('documents:document_metadata', 'Document Metadata')}
                    </Typography>
                    
                    {/* Creator Information */}
                    {document.creator && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 3,
                        flexDirection: isRtl ? 'row' : 'row-reverse',
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha('#ffffff', 0.7),
                        border: '1px solid',
                        borderColor: alpha(theme.palette.divider, 0.7)
                      }}>
                        <Avatar 
                          sx={{ 
                            width: 50, 
                            height: 50,
                            mr: isRtl ? 2.5 : 0,
                            ml: isRtl ? 0 : 2.5,
                            bgcolor: 'primary.main',
                            boxShadow: '0 3px 10px 0 rgba(0,0,0,0.15)'
                          }}
                        >
                          {document.creator.full_name ? document.creator.full_name.charAt(0).toUpperCase() : 
                           document.creator.username ? document.creator.username.charAt(0).toUpperCase() : 'U'}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, textAlign: isRtl ? 'right' : 'left' }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {t('documents:created_by')}
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '1rem'
                            }}
                          >
                            {document.creator.full_name || document.creator.username}
                          </Typography>
                          {document.creator.email && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {document.creator.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Current Assignee Information */}
                    {document.currentAssignee && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 3,
                        flexDirection: isRtl ? 'row' : 'row-reverse',
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha('#e3f2fd', 0.7),
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.3)
                      }}>
                        <Avatar 
                          sx={{ 
                            width: 45, 
                            height: 45,
                            mr: isRtl ? 2.5 : 0,
                            ml: isRtl ? 0 : 2.5,
                            bgcolor: 'info.main',
                            boxShadow: '0 3px 10px 0 rgba(0,0,0,0.15)'
                          }}
                        >
                          {document.currentAssignee.full_name ? document.currentAssignee.full_name.charAt(0).toUpperCase() : 
                           document.currentAssignee.username ? document.currentAssignee.username.charAt(0).toUpperCase() : 'A'}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, textAlign: isRtl ? 'right' : 'left' }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {t('documents:current_assignee')}
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '1rem'
                            }}
                          >
                            {document.currentAssignee.full_name || document.currentAssignee.username}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    <LabelValue 
                      label={t('documents:created_at')} 
                      value={document.created_at ? 
                        <LocalizedDate date={document.created_at} showTime /> : 
                        '—'
                      }
                      icon={<CalendarIcon fontSize="small" />}
                      isRtl={isRtl}
                    />
                    
                    <LabelValue 
                      label={t('documents:last_updated', 'Last Updated')} 
                      value={document.updated_at ? 
                        <LocalizedDate date={document.updated_at} showTime /> : 
                        '—'
                      }
                      icon={<CalendarIcon fontSize="small" />}
                      isRtl={isRtl}
                    />
                    
                    {document.archive_number && (
                      <LabelValue 
                        label={t('documents:archive_number')} 
                        value={document.archive_number}
                        icon={<FileCopyIcon fontSize="small" />}
                        isRtl={isRtl}
                      />
                    )}
                    
                    {document.archive_registration_date && (
                      <LabelValue 
                        label={t('documents:archive_registration_date')} 
                        value={<LocalizedDate date={document.archive_registration_date} />}
                        icon={<CalendarIcon fontSize="small" />}
                        isRtl={isRtl}
                      />
                    )}
                    
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>
         
        </>
      ) : null}
      
      {/* Delete confirmation dialog */}
      <ConfirmDialog />
    </Box>
  );
}

export default DocumentView;
